import {
    useLayoutEffect,
    type FC,
    useRef
} from "react";
import {
    Platform
} from "react-native";
import type IAnimatedIconLayerProps from "./type";
import type {
    AnimatedIconWebShape
} from "./type";
import stylesheet, {
    getAnimatedIconLayerBlur
} from "./stylesheet";
import {
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useReducedMotion
} from "../../../../helpers/motion";
import type {
    AnimatedIconNode
} from "../../type";
import type {
    AnimatedIconPartAttributes
} from "../parts/type";
import Svg, {
    type PolylineProps,
    type EllipseProps,
    type PolygonProps,
    type CircleProps,
    type PathProps,
    type LineProps,
    type RectProps,
    Polyline,
    Ellipse,
    Polygon,
    Circle,
    Path,
    Line,
    Rect
} from "react-native-svg";
import {
    ANIMATED_ICON_REST_PROGRESS,
    ANIMATED_ICON_VIEW_BOX
} from "../../constant";
import {
    getAnimatedIconPartAttributes
} from "../parts/stylesheet";
import {
    webStyle
} from "../../../../utils";

const WEB_ATTRIBUTE_NAMES: Record<keyof AnimatedIconPartAttributes, string> = {
    strokeDashoffset: "stroke-dashoffset",
    strokeDasharray: "stroke-dasharray",
    strokeWidth: "stroke-width",
    transform: "transform",
    opacity: "opacity"
};

const WEB_ATTRIBUTE_KEYS = Object.keys(WEB_ATTRIBUTE_NAMES) as Array<keyof AnimatedIconPartAttributes>;

const AnimatedIconLayer: FC<IAnimatedIconLayerProps> = ({
    isRestHidden,
    customTheme,
    isCrossfade,
    progress,
    color,
    nodes,
    parts,
    value,
    size
}) => {
    const {
        shapeConfigs
    } = NCoreUIKitTheme.useContext(customTheme);

    const isReduceMotionEnabled = useReducedMotion();

    const shapesRef = useRef<Array<AnimatedIconWebShape | null>>([]);

    const isWebDriven = Platform.OS === "web" && !!value;

    useLayoutEffect(() => {
        if(!isWebDriven || !value) {
            return;
        }

        const written = new Map<string, string>();

        const handleUpdate = (nextProgress: number) => {
            const elements = shapesRef.current.map((shape) => shape?.elementRef.current ?? null);

            const svg = elements.find((element) => element)?.ownerSVGElement;

            if(svg) {
                const blur = getAnimatedIconLayerBlur({
                    progress: nextProgress,
                    parts,
                    size
                });

                svg.style.filter = blur > 0 ? `blur(${blur.toFixed(2)}px)` : "";

                if(isRestHidden) {
                    svg.style.display = nextProgress < ANIMATED_ICON_REST_PROGRESS ? "" : "none";
                }
            }

            nodes.forEach((node, index) => {
                const part = parts[index];

                const element = elements[index];

                if(!part || !element || (!isCrossfade && isReduceMotionEnabled)) {
                    return;
                }

                const attributes = getAnimatedIconPartAttributes({
                    strokeWidth: shapeConfigs.iconStrokeWidth,
                    progress: nextProgress,
                    part,
                    node
                });

                WEB_ATTRIBUTE_KEYS.forEach((key) => {
                    const attribute = attributes[key];

                    if(attribute === undefined) {
                        return;
                    }

                    const name = WEB_ATTRIBUTE_NAMES[key];

                    const nextValue = String(attribute);

                    const cacheKey = `${index}:${name}`;

                    if(written.get(cacheKey) === nextValue) {
                        return;
                    }

                    written.set(cacheKey, nextValue);

                    element.setAttribute(name, nextValue);
                });
            });
        };

        const listenerId = value.addListener(({
            value: nextProgress
        }) => {
            handleUpdate(nextProgress);
        });

        return () => {
            value.removeListener(listenerId);
        };
    }, [
        isReduceMotionEnabled,
        isWebDriven,
        isRestHidden,
        shapeConfigs.iconStrokeWidth,
        isCrossfade,
        parts,
        nodes,
        value,
        size
    ]);

    const blur = getAnimatedIconLayerBlur({
        progress,
        parts,
        size
    });

    const getShapeRef = (index: number) => (instance: unknown) => {
        shapesRef.current[index] = isWebDriven ? instance as AnimatedIconWebShape | null : null;
    };

    const renderNode = (node: AnimatedIconNode, index: number) => {
        const part = parts[index];

        const attributes: AnimatedIconPartAttributes = part && (isCrossfade || !isReduceMotionEnabled) ? getAnimatedIconPartAttributes({
            strokeWidth: shapeConfigs.iconStrokeWidth,
            progress,
            part,
            node
        }) : {};

        const props = {
            strokeWidth: shapeConfigs.iconStrokeWidth,
            ...node.attributes,
            ...attributes,
            strokeLinejoin: "round",
            strokeLinecap: "round",
            stroke: color,
            fill: "none"
        } as const;

        if(node.tag === "circle") {
            return <Circle
                {...props as CircleProps}
                ref={getShapeRef(index)}
                key={index}
            />;
        }

        if(node.tag === "rect") {
            return <Rect
                {...props as RectProps}
                ref={getShapeRef(index)}
                key={index}
            />;
        }

        if(node.tag === "line") {
            return <Line
                {...props as LineProps}
                ref={getShapeRef(index)}
                key={index}
            />;
        }

        if(node.tag === "ellipse") {
            return <Ellipse
                {...props as EllipseProps}
                ref={getShapeRef(index)}
                key={index}
            />;
        }

        if(node.tag === "polygon") {
            return <Polygon
                {...props as PolygonProps}
                ref={getShapeRef(index)}
                key={index}
            />;
        }

        if(node.tag === "polyline") {
            return <Polyline
                {...props as PolylineProps}
                ref={getShapeRef(index)}
                key={index}
            />;
        }

        return <Path
            {...props as PathProps}
            ref={getShapeRef(index)}
            key={index}
        />;
    };

    return <Svg
        style={[
            stylesheet.svg,
            blur > 0 ? webStyle({
                filter: `blur(${blur.toFixed(2)}px)`
            }) : null,
            isRestHidden && progress >= ANIMATED_ICON_REST_PROGRESS ? webStyle({
                display: "none"
            }) : null
        ]}
        viewBox={ANIMATED_ICON_VIEW_BOX}
        height={size}
        width={size}
        fill="none"
    >
        {nodes.map(renderNode)}
    </Svg>;
};
export default AnimatedIconLayer;
